import React from 'react';
import {
  Company,
  Appointment,
  Service,
  Venue,
  AddOn,
  MedicalCertificate,
  SystemConfig
} from '@/types';
import { Plus, FileText, Users, Building2, Key, Loader2 } from 'lucide-react';
import { BookingFlow } from '@features/booking/components/BookingFlow';
import { CompanyDashboardModals } from './CompanyDashboardModals';
import { CompanyDashboardTeamTab } from './CompanyDashboardTeamTab';
import { CompanyDashboardSecurityTab } from './CompanyDashboardSecurityTab';
import { CompanyDashboardDashboardTab } from './CompanyDashboardDashboardTab';
import { useCompanyDashboard } from '../hooks/useCompanyDashboard';

interface CompanyDashboardProps {
  isRefreshing?: boolean;
  company: Company;
  appointments: Appointment[];
  services: Service[];
  venues: Venue[];
  addOns: AddOn[];
  certificates: MedicalCertificate[];
  systemConfig?: SystemConfig;
  termsAndConditions: string;
  onBookNew: (appointment: Appointment) => Promise<void> | void;
  onUpdateCompany: (company: Company) => void;
  onForceRefresh?: () => Promise<void>;
}

export const CompanyDashboard: React.FC<CompanyDashboardProps> = ({
  isRefreshing = false,
  company,
  appointments,
  services,
  venues,
  addOns,
  certificates,
  systemConfig,
  termsAndConditions,
  onBookNew,
  onForceRefresh
}) => {
  const {
    view,
    setView,
    search,
    setSearch,
    setBookingsPage,
    bookingsPageSize,
    setBookingsPageSizeAndReset,
    bookingsPagination,
    setTeamPage,
    setTeamPageSizeAndReset,
    teamPageSize,
    isPrimaryAccount,
    companyApps,
    filteredApps,
    activeEmployees,
    teamMembers,
    teamPagination,
    getDashboardBookingRowData,
    servicesById,
    venuesById,
    addOnsById,
    isAddingTeamMember,
    newMember,
    setNewMember,
    memberError,
    isSavingMember,
    openAddTeamMember,
    closeAddTeamMember,
    handleAddTeamMember,
    editingMember,
    setEditingMember,
    teamEditError,
    isSavingTeamEdit,
    closeEditMember,
    handleSaveTeamMember,
    resettingMemberId,
    oldPassword,
    newPassword,
    confirmPassword,
    passwordError,
    isUpdatingPassword,
    setOldPassword,
    setNewPassword,
    setConfirmPassword,
    handlePasswordUpdate,
    editingBooking,
    setEditingBooking,
    isLoadingEditingAvailability,
    editingTimeOptions,
    bookingActionError,
    isSavingBooking,
    handleSaveBooking,
    openEditBooking,
    closeEditBooking,
    handleCancelBooking,
    memberToDelete,
    deleteStep,
    openDeleteMember,
    closeDeleteMember,
    executeDelete,
    handleResetMemberPassword,
    handleViewCertificate
  } = useCompanyDashboard({
    company,
    appointments,
    services,
    venues,
    addOns,
    certificates,
    systemConfig,
    onForceRefresh
  });

  if (view === 'new_booking') {
    return (
      <BookingFlow
        venues={venues}
        services={services}
        addOns={addOns}
        servicesById={servicesById}
        venuesById={venuesById}
        addOnsById={addOnsById}
        appointments={appointments}
        authenticatedCompany={company}
        termsAndConditions={termsAndConditions}
        onComplete={async (app) => {
          await Promise.resolve(onBookNew(app));
          setView('dashboard');
        }}
        onBack={() => setView('dashboard')}
      />
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 animate-fade-in relative">
      <CompanyDashboardModals
        editingBooking={editingBooking}
        setEditingBooking={setEditingBooking}
        isLoadingEditingAvailability={isLoadingEditingAvailability}
        editingTimeOptions={editingTimeOptions}
        bookingActionError={bookingActionError}
        isSavingBooking={isSavingBooking}
        onCloseEditBooking={closeEditBooking}
        onSaveBooking={handleSaveBooking}
        onCancelBooking={handleCancelBooking}
        isAddingTeamMember={isAddingTeamMember}
        newMember={newMember}
        setNewMember={setNewMember}
        memberError={memberError}
        isSavingMember={isSavingMember}
        onCloseAddTeamMember={closeAddTeamMember}
        onAddTeamMember={handleAddTeamMember}
        editingMember={editingMember}
        setEditingMember={setEditingMember}
        teamEditError={teamEditError}
        isSavingTeamEdit={isSavingTeamEdit}
        onCloseEditMember={closeEditMember}
        onSaveTeamMember={handleSaveTeamMember}
        venues={venues}
        memberToDelete={memberToDelete}
        deleteStep={deleteStep}
        onExecuteDelete={executeDelete}
        onCloseDeleteMember={closeDeleteMember}
      />
      {isRefreshing && (
        <div className="absolute inset-0 z-[80] flex items-center justify-center pointer-events-none">
          <div className="absolute inset-0 bg-white/70 backdrop-blur-sm" />
          <div className="relative z-10 bg-white border border-slate-200 shadow-sm rounded-full px-4 py-2 flex items-center gap-2 text-xs font-bold text-slate-700 uppercase tracking-wider">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-purple-600" />
            Refreshing records
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-purple-600" /> Company Portal
          </h2>
          <p className="text-slate-500">
            Managing {company.name}
            <span className="ml-2 text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 uppercase">
              {company.loggedInMember?.name || 'Primary User'}
            </span>
          </p>
        </div>
        <div className="flex gap-3 bg-white p-1 rounded-xl border shadow-sm">
          <button
            onClick={() => setView('dashboard')}
            className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-all ${view === 'dashboard' ? 'bg-purple-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}
          >
            <FileText className="w-4 h-4" /> Bookings
          </button>
          <button
            onClick={() => setView('team')}
            className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-all ${view === 'team' ? 'bg-purple-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}
          >
            <Users className="w-4 h-4" /> Team
          </button>
          <button
            onClick={() => setView('security')}
            className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-all ${view === 'security' ? 'bg-purple-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}
          >
            <Key className="w-4 h-4" /> Password
          </button>
          <button
            onClick={() => setView('new_booking')}
            className="bg-slate-900 text-white px-4 py-2 rounded-lg font-bold hover:bg-slate-800 flex items-center gap-2 shadow-sm ml-2"
          >
            <Plus className="w-5 h-5" /> New Booking
          </button>
        </div>
      </div>

      {view === 'dashboard' && (
        <CompanyDashboardDashboardTab
          companyAppsCount={companyApps.length}
          activeEmployees={activeEmployees}
          paymentPreference={company.paymentPreference}
          filteredAppsCount={filteredApps.length}
          bookingsPagination={bookingsPagination}
          isRefreshing={isRefreshing}
          search={search}
          onSearchChange={setSearch}
          onOpenEditBooking={openEditBooking}
          onViewCertificate={handleViewCertificate}
          onBookingsPageChange={setBookingsPage}
          onBookingsPageSizeChange={(size) => {
            setBookingsPageSizeAndReset(size);
          }}
          bookingRowData={getDashboardBookingRowData}
          bookingsPageSize={bookingsPageSize}
        />
      )}

      {view === 'team' && (
        <CompanyDashboardTeamTab
          company={company}
          isPrimaryAccount={isPrimaryAccount}
          teamMembers={teamMembers}
          teamPagination={teamPagination}
          isRefreshing={isRefreshing}
          isResettingMemberId={resettingMemberId}
          teamPageSize={teamPageSize}
          onOpenAddTeamMember={openAddTeamMember}
          onEditMember={(member) => setEditingMember({ ...member })}
          onResetMemberPassword={handleResetMemberPassword}
          onDeleteMember={openDeleteMember}
          onTeamPageChange={setTeamPage}
          onTeamPageSizeChange={(size) => {
            setTeamPageSizeAndReset(size);
          }}
        />
      )}

      {view === 'security' && (
        <CompanyDashboardSecurityTab
          oldPassword={oldPassword}
          newPassword={newPassword}
          confirmPassword={confirmPassword}
          passwordError={passwordError}
          isUpdatingPassword={isUpdatingPassword}
          onOldPasswordChange={setOldPassword}
          onNewPasswordChange={setNewPassword}
          onConfirmPasswordChange={setConfirmPassword}
          onSubmit={handlePasswordUpdate}
        />
      )}
    </div>
  );
};
